import React, { useEffect, useRef, useState } from "react";
import "./style.scss";
import Carousel from "components/BaseWidgets/carousel";
import Logo from "assets/images/png/yellow-heart.png";
import Heart from "assets/images/svg/story-heart.svg";
import { CarouselPreviousArrow } from "assets/icons";
import Button from "components/BaseWidgets/button";
import Avatar from "components/BaseWidgets/Avatar";
import DOMPurify from "dompurify";

const responsive = [
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 1.5,
    },
  },
  {
    breakpoint: 991,
    settings: {
      slidesToShow: 1.5,
    },
  },
  {
    breakpoint: 892,
    settings: {
      slidesToShow: 1.4,
    },
  },
  {
    breakpoint: 840,
    settings: {
      slidesToShow: 1.3,
    },
  },

  {
    breakpoint: 767,
    settings: {
      slidesToShow: 1.2,
    },
  },

  {
    breakpoint: 720,
    settings: {
      slidesToShow: 1.1,
    },
  },
  {
    breakpoint: 670,
    settings: {
      slidesToShow: 1,
    },
  },
  {
    breakpoint: 615,
    settings: {
      slidesToShow: 0.5,
    },
  },
  {
    breakpoint: 520,
    settings: {
      slidesToShow: 0.5,
      centerPadding: "35%",
    },
  },
];

function JourneysCarousel({
  carousalResponsive = responsive,
  successStories = [],
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef(null);

  const isPrevDisabled = currentSlide === 0;
  const isNextDisabled = currentSlide === successStories.length - 1;

  const handlePrev = () => {
    if (!isPrevDisabled) {
      carouselRef.current?.prev();
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (!isNextDisabled) {
      carouselRef.current?.next();
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const LoveStorySlide = ({
    firstPartner,
    secondPartner,
    date,
    text,
    user,
    partner,
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isClamped, setIsClamped] = useState(false);
    const descRef = useRef(null);

    useEffect(() => {
      if (descRef.current) {
        setIsClamped(
          descRef.current.scrollHeight > descRef.current.clientHeight,
        );
      }
    }, [text]);

    const handleLinkClick = (e) => {
      const target = e.target.closest("a");
      if (target) {
        e.preventDefault();
        e.stopPropagation();
        const url = target.getAttribute("href");
        if (url) {
          window.open(
            /^https?:\/\//i.test(url) ? url : `https://${url}`,
            "_blank",
            "noopener,noreferrer",
          );
        }
      }
    };

    return (
      <div className="love-success-story">
        <div className="couples-profile">
          <span className="heart-icon">
            <img src={Heart} alt="" />
          </span>
          <div className="profiles-wrapper flex gap-2">
            <span className="profile">
              <Avatar text={firstPartner} url={user?.profile_image} size={32} />
            </span>
            <span className="profile">
              <Avatar
                text={secondPartner}
                url={partner?.profile_image}
                size={32}
              />
            </span>
          </div>
        </div>
        <div className="card-details flex flex-col gap-1">
          <span className="couples-name font-title-xs fw-700">
            {`${firstPartner} & ${secondPartner}`}
          </span>
          <span className="post-date">Posted on: {date}</span>
        </div>

        <div
          ref={descRef}
          className={`story font-title-xs fw-400 ${!isExpanded ? "story--clamped" : ""}`}
          onClick={handleLinkClick}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(text || "", {
              ADD_ATTR: ["target", "rel"],
            }),
          }}
        />

        {(isClamped || isExpanded) && (
          <Button
            className="read-more-btn"
            variant="link"
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? "Read less" : "Read more"}
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      {successStories.length > 0 && (
        <div className="journeys-carousel-frame flex flex-col gap-6">
          <span className="brand-logo">
            <img src={Logo} alt="logo" />
          </span>
          <div className="title-button-frame flex justify-between items-end">
            <div className="journeys-carousel-header flex flex-col">
              <span className="title">Our matches, their stories</span>
              <span className="font-title-xl">
                Real couples, genuine connections, inspiring love journeys
              </span>
            </div>
            <div className="flex items-center gap-3 mr-10">
              <Button
                variant="custom"
                onClick={handlePrev}
                isDisabled={isPrevDisabled}
                className={`custom-arrow custom-prev-arrow ${isPrevDisabled ? "disabled" : ""}`}
              >
                <CarouselPreviousArrow />
              </Button>
              <Button
                variant="custom"
                onClick={handleNext}
                isDisabled={isNextDisabled}
                className={`custom-arrow custom-next-arrow ${isNextDisabled ? "disabled" : ""}`}
              >
                <CarouselPreviousArrow />
              </Button>
            </div>
          </div>
          <div className="journeys-carousel-content">
            <Carousel
              slidesToShow={1.8}
              ref={carouselRef}
              slides={successStories}
              centerPadding="0%"
              autoplaySpeed={4000}
              autoplay={{ dotDuration: false }}
              responsive={carousalResponsive}
              renderItem={(item) => (
                <div key={item.id} className="carousel-slide-wrapper">
                  <div className="carousel-slide-content flex items-start gap-1">
                    <div className="carousel-image-wrapper">
                      {item.images?.[0]?.image && (
                        <img
                          src={item.images[0].image}
                          alt={`Success story: ${item.title}`}
                          className="carousel-image"
                        />
                      )}
                    </div>
                    <LoveStorySlide
                      firstPartner={item?.user?.first_name || ""}
                      secondPartner={item?.partner?.first_name || ""}
                      date={item?.created_at}
                      text={item?.body}
                      user={item?.user}
                      partner={item?.partner}
                    />
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default JourneysCarousel;
